import { asArray } from './promoPrimitives';

/**
 * Read access to the promo step snapshot built by the legacy `visitStep3PromoVM.buildPromoSnapshot()` (#23521).
 *
 * The snapshot carries raw data plus the legacy classifications (period levels, offer visibility, product rights,
 * prices, objectives). Every promo rule is computed from it by this domain.
 *
 * @typedef {Object} PromoRow  legacy `itemCampaignOffersModel` (appointmentModel.js), or the plain preco row of `updateVolumePreco`
 * @typedef {{ campaignId: number, campaignOfferId: number, offerKey: (string|null) }} OfferRef
 *   offerKey is the `_key` of a local ("OP locale" / pirate) offer, null for a national offer.
 */

export const NEGO = 'nego';
export const STATED = 'stated';

const ROWS_KEY = { [NEGO]: '_negociatedCampaignOffers', [STATED]: '_statedCampaignOffers' };
const MODEL_KEY = { [NEGO]: 'negotiated', [STATED]: 'stated' };

export const getCampaign = (snapshot, campaignId) => asArray(snapshot?.campaigns).find((c) => c.id === campaignId) || null;

export const getOffer = (campaign, offerId) => asArray(campaign?.offers).find((o) => o.id === offerId) || null;

export const getOfferInfo = (snapshot, offerKey) => (offerKey ? asArray(snapshot?.model?.offerInfos).find((oi) => oi._key === offerKey) || null : null);

/** The offerInfo a national offer keeps its comment / statement end date on (legacy `_.find(offerInfos, {campaignId, campaignOfferId})`). */
export const getNationalOfferInfo = (snapshot, campaignId, offerId) => asArray(snapshot?.model?.offerInfos).find((oi) => oi.campaignId === campaignId && oi.campaignOfferId === offerId) || null;

export const getLocalOfferInfos = (snapshot, campaignId) => asArray(snapshot?.model?.offerInfos).filter((oi) => oi.local && oi.campaignId === campaignId);

export const getProduct = (snapshot, productId) => snapshot?.products?.[productId] || null;

export const toRef = (campaignId, campaignOfferId, offerKey = null) => ({ campaignId, campaignOfferId, offerKey: offerKey || null });

export const refOfOfferInfo = (offerInfo) => toRef(offerInfo.campaignId, offerInfo.campaignOfferId, offerInfo._key);

/** Rows of one offer scope : the appointment-level arrays for a national offer, the offerInfo arrays for a local one. */
export function rowsOf(snapshot, ref, kind) {
	if (ref.offerKey) return asArray(getOfferInfo(snapshot, ref.offerKey)?.[ROWS_KEY[kind]]);
	return asArray(snapshot?.model?.[MODEL_KEY[kind]]).filter((row) => row.campaignId === ref.campaignId && row.campaignOfferId === ref.campaignOfferId);
}

export const negoRowsOf = (snapshot, ref) => rowsOf(snapshot, ref, NEGO);
export const statedRowsOf = (snapshot, ref) => rowsOf(snapshot, ref, STATED);

const sameProduct = (row, ref, productId) => row.campaignId === ref.campaignId && row.campaignOfferId === ref.campaignOfferId && row.productId === productId;

/** The paying main row of a product : not additional, not free. */
export const findMainRow = (rows, ref, productId) => asArray(rows).find((row) => sameProduct(row, ref, productId) && !row.additional && !row.isFree) || null;

/** The main constat row : not additional, not the statement of the free line (#23572). */
export const findMainStatedRow = (rows, ref, productId) => asArray(rows).find((row) => sameProduct(row, ref, productId) && !row.additional && !row.isFree) || null;

export const findFreeRow = (rows, ref, productId) => asArray(rows).find((row) => sameProduct(row, ref, productId) && row.isFree && !row.additional) || null;

export const additionalRowsOf = (rows, ref, productId) => asArray(rows).filter((row) => sameProduct(row, ref, productId) && row.additional);

/** Containers a product can be negotiated in for one offer : the containers of its published offer rows, with their UC packing. */
export function containersOf(snapshot, offer, productId) {
	const product = getProduct(snapshot, productId);
	const labels = snapshot?.refs?.containers || [];
	const ids = asArray(offer?.products)
		.filter((p) => p.published && p.productId === productId)
		.map((p) => Number(p.productContainerId));
	return ids
		.map((id) => {
			const container = labels.find((c) => c.id === id);
			if (!container) return null;
			return { id, label: container.label, ucQty: Number(product?.packing?.[id]) || 0 };
		})
		.filter(Boolean);
}

/**
 * Products listed in an offer, one entry per product (legacy `getOfferProducts`, grouped by productId : underscore's
 * groupBy over numeric keys yields them in ascending productId order).
 * For a local offer only the products added to it are listed (`loadLocaleOfferProductsSource`).
 */
export function listedProductIds(snapshot, campaign, offer, offerInfo = null) {
	let ids = [
		...new Set(
			asArray(offer?.products)
				.filter((p) => p.published)
				.map((p) => p.productId)
		),
	].sort((a, b) => a - b);
	if (campaign?.promoLocal && offerInfo) {
		const negotiated = asArray(offerInfo._negociatedCampaignOffers).map((row) => row.productId);
		ids = ids.filter((id) => negotiated.includes(id));
	}
	return ids.filter((id) => isProductReadable(snapshot, id));
}

/** A product missing from the assortment, or outside the user's read rights (TFS#20608), is not listed. */
export function isProductReadable(snapshot, productId) {
	const product = getProduct(snapshot, productId);
	return !!product && product.readable !== false;
}

/**
 * The model as it will be once a commit patch is stored by the legacy (`commitPromoChange`), so the rules depending on
 * the whole model (promo plan selection) can be evaluated before committing.
 */
export function modelWithPatch(model, patch) {
	const scope = patch?.scope || {};
	const inScope = (row) => row.campaignId === scope.campaignId && row.campaignOfferId === scope.campaignOfferId;
	const next = { ...model, negotiated: asArray(model?.negotiated), stated: asArray(model?.stated), offerInfos: asArray(model?.offerInfos) };
	if (scope.offerKey) {
		next.offerInfos = next.offerInfos.map((oi) => (oi._key !== scope.offerKey ? oi : { ...oi, _negociatedCampaignOffers: patch.negotiated || oi._negociatedCampaignOffers, _statedCampaignOffers: patch.stated || oi._statedCampaignOffers, ...(patch.offerInfo || {}) }));
	} else {
		if (patch?.negotiated) next.negotiated = [...next.negotiated.filter((row) => !inScope(row)), ...patch.negotiated];
		if (patch?.stated) next.stated = [...next.stated.filter((row) => !inScope(row)), ...patch.stated];
	}
	if (patch?.newOfferInfos) next.offerInfos = [...next.offerInfos, ...patch.newOfferInfos.map((oi) => ({ _negociatedCampaignOffers: [], _statedCampaignOffers: [], photosStatements: [], ...oi }))];
	if (patch?.removedOfferKey) next.offerInfos = next.offerInfos.filter((oi) => oi._key !== patch.removedOfferKey);
	if (patch?.displayOnPromoPlan) next.displayOnPromoPlan = patch.displayOnPromoPlan;
	if (patch?.promoPlanOffers) next.promoPlanOffers = { ...(model?.promoPlanOffers || {}), ...patch.promoPlanOffers };
	return next;
}

/** A blank legacy `itemCampaignOffersModel`, with the same defaults (`local` stays false unless the caller sets it, as in the legacy). */
export function newRow(ref, productId, fields = {}) {
	return {
		offerKey: ref.offerKey || '',
		campaignId: ref.campaignId,
		campaignOfferId: ref.campaignOfferId,
		productId,
		locationPromoId: 0,
		productContainerId: 0,
		additional: false,
		volume: 0,
		locationHighlightId: 0,
		volumeUC: 0,
		value: 0,
		_totalValue: false,
		present: 0,
		precoVolumeUC: 0,
		precoValue: 0,
		statedValue: 0,
		deliveryLocationId: 0,
		local: false,
		isFree: false,
		...fields,
	};
}
